<template>


  <div class="center-flex">
    <div class="vertical-stack">
      <Panel toggleable header="Filters" >
        <div class="filter-section">
          <label class="slider-label">Data Type Filter</label>
          <MultiSelect
            v-if="showFilter"
            v-model="selectedFilters"
            :options="filterOptions"
            placeholder="Select data types"
            class="stretch-horizontal"
            :maxSelectedLabels="3"
            selectedItemsLabel="{0} items selected"
          />
        </div>
        
        <div class="filter-section">

          <DateRangeSlider 
            name="Date Range Filter"
            :start="dateRange.min"
            :end="dateRange.max"
            :modelValue="[startDate, endDate]"
            @update:modelValue="onDateRangeChange"
            :step="1"
          />
        </div>
        <div class="filter-section">
          <Button label="Clear Filters" @click="clearFilters" />
          <div class="date-info">
            <small class="text-muted">
              {{ parsedDates.length }} of {{ files.length }} files have dates in filename
            </small>
          </div>
        </div>
        <Divider />
        <div class="flex justify-end mb-2 gap-2">
          <Button 
            icon="pi pi-palette" 
            :label="fccMode ? 'Exit FCC Mode' : 'FCC Mode'" 
            @click="$emit('toggle-fcc')"
            class="p-button-sm"
            :severity="fccMode ? 'success' : 'secondary'"
          />
          <Button 
            icon="pi pi-download" 
            label="Download Selected" 
            @click="downloadSelectedFiles"
            :disabled="!hasSelectedFiles"
            class="p-button-sm"
          />
        </div>
      </Panel>
      <div v-if="filteredFiles.length === 0">
        <thead>
          <tr>
            <th>No files found for site.</th>
          </tr>
        </thead>
      </div>
      <div v-else>
        <table class="stretch-horizontal">
          <thead>
            <tr>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in filteredFiles" :key="file.name" @click="onRowClick(file)">
              <td>
                <div class="flex items-center">
                  <Checkbox 
                    v-model="selectedFiles"
                    :value="file"
                    class="mr-2"
                    @click.stop
                    @change="onCheckboxChange(file)"
                    :id="file.name"
                  />
                  <div class="file-info">
                    <div class="file-display-name">
                      {{ getFileDisplayName(file) }}
                    </div>
                    <div class="file-details">
                      <small class="text-muted">
                        {{ file.name }}
                        <span v-if="file.size_human"> • {{ file.size_human }}</span>
                      </small>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from 'vue'
import type { File } from '@/composables/useFiles'
import type { PropType } from 'vue'
import CustomDatepicker from '@/components/CustomDatepicker.vue';
import { MultiSelect, Button, Checkbox } from 'primevue';
import Dateslider from '@/components/DateRangeSlider.vue';
import DateRangeSlider from '@/components/DateRangeSlider.vue';

export default {
  name: 'FileList',
  emits: ['file-click', 'file-unload', 'add-to-fcc', 'toggle-fcc'],
  props: {
    siteName: {
      type: String,
    },
    files: {
      type: Array as PropType<File[]>,
      required: true
    },
    fccMode: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CustomDatepicker,
    DateRangeSlider,
    MultiSelect,
    Button,
    Checkbox
  },
  setup(props, { emit }) {
    const showFilter = ref(true)
    const selectedFilters = ref<string[]>([])
    const selectedFiles = ref<File[]>([])

    // Parse dates from filenames and find extremes
    const parsedDates = computed(() => {
      const dates: Date[] = []
      props.files.forEach(file => {
        // First try to use the parsed date from the API
        if (file.date) {
          const apiDate = new Date(file.date)
          if (!isNaN(apiDate.getTime())) {
            dates.push(apiDate)
            return
          }
        }
        
        // Fallback to parsing filename manually (for backwards compatibility)
        const dateMatch = file.name.match(/(\d{8})(?:t|\.tiff?)/);
        if (dateMatch) {
          const fileDate = new Date(
            dateMatch[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
          );
          if (!isNaN(fileDate.getTime())) {
            dates.push(fileDate);
          }
        }
      });
      return dates.sort((a, b) => a.getTime() - b.getTime());
    })

    // Get date bounds from parsed dates or use fallback
    const dateRange = computed(() => {
      if (parsedDates.value.length > 0) {
        return {
          min: parsedDates.value[0],
          max: parsedDates.value[parsedDates.value.length - 1]
        }
      }
      // Fallback if no dates found in filenames
      return {
        min: new Date('2000-01-01'),
        max: new Date('2030-12-31')
      }
    })

    // Initialize filter dates to the full range
    const startDate = ref<Date>(dateRange.value.min)
    const endDate = ref<Date>(dateRange.value.max)
    
    // Watch for changes in file list to update date range
    watch(() => props.files, () => {
      startDate.value = dateRange.value.min
      endDate.value = dateRange.value.max
    }, { immediate: true })
    
    const hasSelectedFiles = computed(() => selectedFiles.value.length > 0)

    const downloadSelectedFiles = async () => {
      for (const file of selectedFiles.value) {
        try {
          const response = await fetch(`/api/media/protected_image/${props.siteName}/${file.name}`)
          if (!response.ok) throw new Error(`Failed to download ${file.name}`)
          
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = file.name
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        } catch (error) {
          console.error(`Error downloading ${file.name}:`, error)
        }
      }
    }

    // Extract first two underscore-separated words or use API type
    const filterOptions = computed(() => {
      const options = props.files.map(file => {
        // If we have API type info, use that
        if (file.type && file.type !== 'unknown') {
          return file.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
        
        // Fallback to extracting from filename
        const parts = file.name.split('_');
        return parts.slice(0, 2).join(' ');
      });
      return [...new Set(options)]; // Ensure unique options
    });

    const clearFilters = () => {
      selectedFilters.value = [];
      startDate.value = dateRange.value.min;
      endDate.value = dateRange.value.max;
    }

    // Handle date range slider changes
    const onDateRangeChange = (range: [Date, Date]) => {
      startDate.value = range[0]
      endDate.value = range[1]
    }

    // Format file display name as "Type - Date" or fallback to filename
    const getFileDisplayName = (file: File) => {
      // If we have detailed file info, format it nicely
      if (file.type && file.type !== 'unknown') {
        let displayName = file.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        
        if (file.date_string) {
          // Format date as YYYY-MM-DD
          const formattedDate = file.date_string.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
          displayName += ` - ${formattedDate}`
        }
        
        return displayName
      }
      
      // Fallback to original filename if no detailed info
      return file.name
    }

    // Filter files based on selected filter and date range
    const filteredFiles = computed(() => {
      let filtered = props.files;
      
      // Apply name filter if selected
      if (selectedFilters.value.length > 0) {
        filtered = filtered.filter(file => {
          // If we have API type info, use that
          if (file.type && file.type !== 'unknown') {
            const formattedType = file.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            return selectedFilters.value.includes(formattedType);
          }
          
          // Fallback to extracting from filename
          const parts = file.name.split('_');
          const fileFilter = parts.slice(0, 2).join(' ');
          return selectedFilters.value.includes(fileFilter);
        });
      }

      // Apply date range filter
      if (startDate.value && endDate.value) {
        filtered = filtered.filter(file => {
          // First try to use the API's parsed date
          if (file.date) {
            const fileDate = new Date(file.date)
            if (!isNaN(fileDate.getTime())) {
              return fileDate >= startDate.value && fileDate <= endDate.value
            }
          }
          
          // Fallback to extracting date from filename (backwards compatibility)
          const dateMatch = file.name.match(/(\d{8})(?:t|\.tiff?)/);
          // If no date in filename, include the file anyway
          if (!dateMatch) return true;
          
          const fileDate = new Date(
            dateMatch[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
          );
          return fileDate >= startDate.value && fileDate <= endDate.value;
        });
      }

      return filtered;
    });

    // Handle checkbox changes - this fires when checkbox state changes
    const onCheckboxChange = (file: File) => {
      // Use nextTick to ensure the selectedFiles array is updated
      setTimeout(() => {
        const isSelected = selectedFiles.value.some(f => f.name === file.name)
        if (isSelected) {
          emit('file-click', file)
        } else {
          emit('file-unload', file)
        }
      }, 0)
    }

    // Handle row clicks - toggle checkbox and emit appropriate event
    const onRowClick = async (file: File) => {
      // If in FCC mode, emit add-to-fcc instead of normal file operations
      if (props.fccMode) {
        emit('add-to-fcc', file)
        return
      }

      const fileIndex = selectedFiles.value.findIndex(f => f.name === file.name)
      console.log('Row clicked:', file.name, 'Index in selectedFiles:', fileIndex)
      
      if (fileIndex >= 0) {
        // File is selected - unselect it by creating new array without this file
        selectedFiles.value = selectedFiles.value.filter(f => f.name !== file.name)
        emit('file-unload', file)
      } else {
        // File is not selected - select it by creating new array with this file
        selectedFiles.value = [...selectedFiles.value, file]
        emit('file-click', file)
      }
      
      // Force Vue to update the DOM
      await nextTick()
    }

    // Emit when a file is clicked - toggle checkbox and handle file loading
    const onFileClick = (file: File) => {
      // Check if file is already selected
      const fileIndex = selectedFiles.value.findIndex(f => f.name === file.name)
      console.log('File clicked:', file.name, 'Index in selectedFiles:', fileIndex)
      if (fileIndex >= 0) {
        // File is already selected - unselect it and emit unload
        selectedFiles.value.splice(fileIndex, 1)
        emit('file-unload', file)
      } else {
        // File is not selected - select it and emit load
        selectedFiles.value.push(file)
        emit('file-click', file)
      }
    }

    const toggleFilter = () => {
      showFilter.value = !showFilter.value
    }

    return { 
      toggleFilter, 
      showFilter, 
      selectedFilters, 
      clearFilters, 
      startDate, 
      endDate, 
      filterOptions, 
      filteredFiles, 
      onFileClick, 
      onRowClick,
      onCheckboxChange,
      selectedFiles, 
      hasSelectedFiles, 
      downloadSelectedFiles,
      dateRange,
      onDateRangeChange,
      parsedDates,
      getFileDisplayName
    }
  }
}
</script>

<style scoped>
.centre-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}
.vertical-stack {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.stretch-horizontal {
  flex-grow: 1;
  width: 95%;
}
.flex {
  display: flex;
}
.items-center {
  align-items: center;
}
.justify-end {
  justify-content: flex-end;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mr-2 {
  margin-right: 0.5rem;
}
.date-info {
  margin-top: 0.5rem;
  text-align: center;
}
.text-muted {
  color: var(--text-color-secondary, #666);
  font-style: italic;
}
.file-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.file-display-name {
  font-weight: 500;
  color: var(--text-color, #333);
}
.file-details {
  font-size: 0.875rem;
}
.filter-section {
  margin-bottom: 1rem;
}
.slider-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color);
  display: block;
  margin-bottom: 0.5rem;
}
</style>
